import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, SubmissionLoader,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  ProductFormSingleTextInput,
  ProductFormDropdownSelect,
} from '@Components/Partial/Product/NewSearch';
import { StyledRaisedContainer } from '@Components/Partial/Product/StyledSharedComponents.jsx';
import {
  initiateFetchFingerprintingConfigRequest,
  clearFingerprintingConfigRequest,
  updateFingerprintingConfigRequest,
} from '@Actions';

const MATCH_TYPE_OPTIONS = ['All', 'Audio Only', 'Audio Visual', 'Video Only'];

const MATCH_TYPE_HINT = 'Filter matches by type of content. Select "All" to include every match type, or restrict to a specific audio/video combination.';

const NUMBER_FIELDS = [
  {
    key: 'minimum_source_coverage',
    stateKey: 'minimumSourceCoverage',
    label: 'Minimum source coverage (%)',
    hint: 'The minimum percentage of the reference asset that must be matched for a result to be reported.',
    min: 0,
    max: 100,
  },
  {
    key: 'minimum_segment_duration',
    stateKey: 'minimumSegmentDuration',
    label: 'Minimum segment duration (s)',
    hint: 'The shortest matching segment, in seconds, that will be considered a valid match.',
    min: 0,
  },
  {
    key: 'minimum_frame_match_count',
    stateKey: 'minimumFrameMatchCount',
    label: 'Minimum frame match count (%)',
    hint: 'The minimum percentage of total source frames that must match for a segment to be counted as a match.',
    min: 0,
    max: 100,
  },
  {
    key: 'minimum_confidence_threshold',
    stateKey: 'minimumConfidenceThreshold',
    label: 'Minimum confidence threshold (%)',
    hint: 'Only matches with a confidence score at or above this value will be surfaced.',
    min: 0,
    max: 100,
  },
  {
    key: 'full_copy_detection_threshold',
    stateKey: 'fullCopyDetectionThreshold',
    label: 'Full copy detection threshold (%)',
    hint: 'Source coverage at or above this percentage will be flagged as a full copy of the reference asset.',
    min: 0,
    max: 100,
  },
];

const DEFAULT_CONFIG = {
  matchTypeFilter: 'All',
  minimumSourceCoverage: 20,
  minimumSegmentDuration: 1,
  minimumFrameMatchCount: 50,
  minimumConfidenceThreshold: 70,
  fullCopyDetectionThreshold: 90,
};

const FingerprintingConfigPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchConfig,
  clearConfig,
  saveConfig,
  fingerprintingConfig = null,
}) => {
  const [formValues, setFormValues] = React.useState(DEFAULT_CONFIG);
  const {
    theme: { page, button, navigation },
    isDarkMode,
  } = useAppTheme();

  React.useEffect(() => {
    fetchConfig();

    return () => {
      clearConfig();
    };
  }, []);

  React.useEffect(() => {
    if (fingerprintingConfig) {
      setFormValues({
        matchTypeFilter: fingerprintingConfig.matchTypeFilter
          ?? DEFAULT_CONFIG.matchTypeFilter,
        minimumSourceCoverage: fingerprintingConfig.minimumSourceCoverage
          ?? DEFAULT_CONFIG.minimumSourceCoverage,
        minimumSegmentDuration: fingerprintingConfig.minimumSegmentDuration
          ?? DEFAULT_CONFIG.minimumSegmentDuration,
        minimumFrameMatchCount: fingerprintingConfig.minimumFrameMatchCount
          ?? DEFAULT_CONFIG.minimumFrameMatchCount,
        minimumConfidenceThreshold: fingerprintingConfig.minimumConfidenceThreshold
          ?? DEFAULT_CONFIG.minimumConfidenceThreshold,
        fullCopyDetectionThreshold: fingerprintingConfig.fullCopyDetectionThreshold
          ?? DEFAULT_CONFIG.fullCopyDetectionThreshold,
      });
    }
  }, [fingerprintingConfig]);

  const handleNumberChange = (stateKey, value) => {
    setFormValues((prev) => ({
      ...prev,
      [stateKey]: value === '' ? '' : Number(value),
    }));
  };

  const buildBody = (values) => ({
    /* eslint-disable camelcase */
    match_type_filter: values.matchTypeFilter,
    minimum_source_coverage: values.minimumSourceCoverage,
    minimum_segment_duration: values.minimumSegmentDuration,
    minimum_frame_match_count: values.minimumFrameMatchCount,
    minimum_confidence_threshold: values.minimumConfidenceThreshold,
    full_copy_detection_threshold: values.fullCopyDetectionThreshold,
    /* eslint-enable camelcase */
  });

  const handleSubmit = () => {
    if (submitLoading) return;
    saveConfig(buildBody(formValues));
  };

  const handleResetToDefaults = () => {
    if (submitLoading) return;
    setFormValues(DEFAULT_CONFIG);
    saveConfig(buildBody(DEFAULT_CONFIG));
  };

  const isLoading = Boolean(loading || submitLoading);
  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Fingerprinting',
        subTitle: 'Settings',
        textColor: 'white',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          message="Saving configuration..."
          contentWidth="20rem"
        />
      )}
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
      >
        <InfoNotice
          header="Fingerprinting configuration information"
          text="Configure thresholds and filters used when matching content fingerprints. Changes take effect on the next fingerprinting run."
        />
        <StyledRaisedContainer
          direction="column"
          gap="1.25rem"
          margin={{ horizontal: '1.5rem' }}
          pad={{ horizontal: '1.5rem', vertical: '1rem' }}
          background={page.cardBg}
          borderColor={navigation.border}
          width="auto"
        >
          <Box
            direction="row"
            justify="between"
            border={{
              color: navigation.border, size: '1px', style: 'solid', side: 'bottom',
            }}
            margin={{ bottom: '0.6rem' }}
          >
            <Box
              flex={false}
              border={{
                color: navigation.border, size: '1px', style: 'solid', side: 'bottom',
              }}
              justify="start"
              pad={{ bottom: '0.5rem' }}
            >
              <Text weight={600} size="1rem" color={page.primaryText}>
                Configuration options
              </Text>
            </Box>
            <Box pad={{ bottom: '0.5rem' }}>
              <AppButton
                overrideHover
                onClick={handleResetToDefaults}
                height="var(--table-controls-height)"
                label="Reset to defaults"
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                disabled={isLoading}
              />
            </Box>
          </Box>
          <Box direction="column" gap="0.5rem">
            <Text weight={500} color={page.primaryText}>Match type filter</Text>
            <Text size="0.75rem" color={page.hintText}>{MATCH_TYPE_HINT}</Text>
            <ProductFormDropdownSelect
              simpleOptions={MATCH_TYPE_OPTIONS}
              selectable
              keepLabel
              value={formValues.matchTypeFilter || null}
              handleFormValues={(val) => setFormValues((prev) => ({
                ...prev,
                matchTypeFilter: val,
              }))}
            />
          </Box>
          {NUMBER_FIELDS.map(({
            key, stateKey, label, hint, min, max,
          }) => (
            <Box key={key} direction="column" gap="0.5rem">
              <Text weight={500} color={page.primaryText}>{label}</Text>
              <Text size="0.75rem" color={page.hintText}>{hint}</Text>
              <ProductFormSingleTextInput
                type="number"
                focusHighlight={page.focusHighlight}
                value={formValues[stateKey]}
                handleFormValues={(val) => handleNumberChange(stateKey, val)}
                min={min}
                max={max}
                disabled={isLoading}
              />
            </Box>
          ))}
          <Box margin={{ top: '0.25rem' }}>
            <AppButton
              overrideHover
              width="12rem"
              disabled={isLoading}
              onClick={handleSubmit}
              level="dynamicLarge"
              color="white"
              bgColor={button.primaryBg}
              fontWeight={600}
              label={submitLoading ? 'Saving...' : 'Save configuration'}
            />
          </Box>
        </StyledRaisedContainer>
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    fingerprintingConfig: state.fingerprinting.fingerprintingConfig,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchConfig: initiateFetchFingerprintingConfigRequest,
    clearConfig: clearFingerprintingConfigRequest,
    saveConfig: updateFingerprintingConfigRequest,
  }, dispatch);
}

FingerprintingConfigPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchConfig: PropTypes.func.isRequired,
  clearConfig: PropTypes.func.isRequired,
  saveConfig: PropTypes.func.isRequired,
  fingerprintingConfig: PropTypes.shape({
    matchTypeFilter: PropTypes.string,
    minimumSourceCoverage: PropTypes.number,
    minimumSegmentDuration: PropTypes.number,
    minimumFrameMatchCount: PropTypes.number,
    minimumConfidenceThreshold: PropTypes.number,
    fullCopyDetectionThreshold: PropTypes.number,
  }),
};

export default connect(
  mapStateToProps, mapDispatchToProps,
)(withProductAuth(FingerprintingConfigPage));
